import React from 'react'
// libs
import styled from 'styled-components'
import { useMutation } from '@apollo/client/react'
import { useDispatch, useSelector } from 'react-redux'
import { arraySplice, change, FieldArray, formValueSelector, getFormValues, reduxForm } from 'redux-form'
// hooks
import { useShowModal } from 'hooks/index'
// graphql
import { PLAN_TYPE_UPDATE_MUTATION } from 'graphql/planType/mutations'
import { OBJECT_SECURITIES_QUERY } from 'graphql/objectSecurity/queries'
// components
import { HoverablePopover } from 'components/UI'
import { AccordionSummary } from 'components/PageComponents/Settings/ObjectsAndProperties/Edit/view'
import { Accordion, AccordionDetails } from 'components/PageComponents/Settings/PlanTypes/Edit/views'
import { ReduxEntitySelect, ReduxInput, ReduxSelect, ReduxSwitch } from 'components/FormElements/ReduxForm'
// view components
import Levels from './components/Levels'
import SetColorModal from 'components/Modals/SetColorModal'
// views
import { Form } from 'themes/views/settings'
import { DescriptionContainer, FieldsContainer } from './views'
// constants
import { Forms, Modals } from 'constants/index'
// graphql
import { INITIAL_PER_PAGE } from 'constants/table'
import { PLAN_TYPES_LIST_QUERY } from 'graphql/planType/queries'
// types
import { IOption } from 'components/FormElements/Select'
import { InjectedFormProps } from 'redux-form/lib/reduxForm'
// themes
import { colors } from 'themes/index'
// icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
// utils
import omit from 'lodash/omit'
import times from 'lodash/times'
import debounce from 'lodash/debounce'
import { v4 as uuid } from 'uuid'
import { required } from 'utils/validators'
import { toSubmissionError } from 'utils/errors/form'
import { getValueFromOption } from 'utils/form'
import { objectSecurityProps } from 'components/FormElements/ReduxForm/ReduxEntitySelect/utils'

const FieldsWrapper = styled.div`
  gap: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding-bottom: 20px;
`

const levelsCountOptions = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 }
]

const SECURITY_OPTION_FIELDS = [
  'objectiveOwnerObjectSecurity',
  'objectiveMemberObjectSecurity',
  'actionOwnerObjectSecurity',
  'actionMemberObjectSecurity',
  'kpiOwnerObjectSecurity',
  'kpiMemberObjectSecurity',
  'planOwnerObjectSecurity',
  'planMemberObjectSecurity'
]

const selector = formValueSelector(Forms.PLAN_TYPE_EDIT_FORM)

interface Props {
  planTypeId: string
}

const PlanTypeForm: React.FC<InjectedFormProps<any, Props> & Props> = ({ planTypeId, handleSubmit }) => {
  const dispatch = useDispatch()

  const { levels, allowNesting } = useSelector((state) => ({
    levels: selector(state, 'levels') || [],
    allowNesting: selector(state, 'allowNesting')
  }))

  const formValues = useSelector(getFormValues(Forms.PLAN_TYPE_EDIT_FORM))

  const [updateEntityMutation] = useMutation(PLAN_TYPE_UPDATE_MUTATION)

  const { showModal } = useShowModal({ modalType: Modals.SET_COLOR_MODAL })

  const [isInfoOpen, setIsInfoOpen] = React.useState(true)
  const [isSecurityOpen, setIsSecurityOpen] = React.useState(true)

  const onPanelClick = (field: string, color: string) => () => {
    showModal({
      width: 'sm',
      initialColor: color,
      onSubmit: ({ color: { hex } }) => dispatch(change(Forms.PLAN_TYPE_EDIT_FORM, `${field}.color`, hex))
    })
  }

  const handleCountChange = ({ prev, next }) => {
    if (prev > next) {
      dispatch(arraySplice(Forms.PLAN_TYPE_EDIT_FORM, 'levels', next, prev - next, null))
    } else if (prev < next) {
      times(next - prev, (i) => {
        dispatch(
          arraySplice(Forms.PLAN_TYPE_EDIT_FORM, 'levels', prev + i, 0, {
            color: colors.purple400,
            position: prev + i
          })
        )
      })
    }
  }

  const handleFormSubmit = React.useCallback(
    async (data) => {
      try {
        await updateEntityMutation({
          variables: {
            id: planTypeId,
            planType: {
              ...omit(data, SECURITY_OPTION_FIELDS),
              levels: (data.levels || []).map((level) => ({
                ...omit(level, '__typename'),
                typeOptions: (level.typeOptions || []).map((typeOption) => ({
                  id: typeOption.id || uuid(),
                  label: typeOption.label
                }))
              })),
              maximumNestedLevelsCount: getValueFromOption(data.maximumNestedLevelsCount),
              objectiveOwnerObjectSecurityId: getValueFromOption(data.objectiveOwnerObjectSecurity),
              objectiveMemberObjectSecurityId: getValueFromOption(data.objectiveMemberObjectSecurity),
              actionOwnerObjectSecurityId: getValueFromOption(data.actionOwnerObjectSecurity),
              actionMemberObjectSecurityId: getValueFromOption(data.actionMemberObjectSecurity),
              kpiOwnerObjectSecurityId: getValueFromOption(data.kpiOwnerObjectSecurity),
              kpiMemberObjectSecurityId: getValueFromOption(data.kpiMemberObjectSecurity),
              planOwnerObjectSecurityId: getValueFromOption(data.planOwnerObjectSecurity),
              planMemberObjectSecurityId: getValueFromOption(data.planMemberObjectSecurity)
            }
          },
          refetchQueries: [
            {
              query: PLAN_TYPES_LIST_QUERY,
              variables: {
                pagination: {
                  pageSkip: 0,
                  pageTake: INITIAL_PER_PAGE
                }
              }
            }
          ]
        })
      } catch (errors) {
        throw toSubmissionError(errors)
      }
    },
    [planTypeId, updateEntityMutation]
  )

  const handleAllowNestingChange = (_e: React.ChangeEvent, value: boolean) => {
    if (value) {
      dispatch(change(Forms.PLAN_TYPE_EDIT_FORM, 'maximumNestedLevelsCount', { label: '2', value: 2 }))
      handleCountChange({ prev: levels.length, next: 2 })
    } else {
      dispatch(change(Forms.PLAN_TYPE_EDIT_FORM, 'maximumNestedLevelsCount', { label: 1, value: 1 }))
      handleCountChange({ prev: levels.length, next: 1 })
    }
  }

  const handleMaximumNestedLevelsCountChange = (value: IOption) => {
    handleCountChange({ prev: levels.length, next: getValueFromOption(value) })
  }

  const debouncedSubmit = React.useCallback(
    debounce(() => handleSubmit(handleFormSubmit)(), 800),
    []
  )

  React.useEffect(() => {
    return () => debouncedSubmit.cancel()
  }, [debouncedSubmit])

  React.useEffect(() => {
    debouncedSubmit()
  }, [formValues])

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <Accordion expanded={isInfoOpen} onChange={(_, expanded) => setIsInfoOpen(expanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon htmlColor={colors.blue500} />}>Details</AccordionSummary>
        <AccordionDetails>
          <FieldsContainer>
            <ReduxSwitch name='active' label='Plan Type Active' justifyContent={'flex-end'} />
            <DescriptionContainer>
              <ReduxInput name='code' label='Code' validate={[required]} />
              <ReduxInput name='description' label='Description' validate={[required]} />
            </DescriptionContainer>
            <ReduxSwitch
              name='allowNesting'
              label={
                <HoverablePopover trigger={<span>Allow nesting?</span>}>
                  This enables parent child relationships to be created within the same plan
                </HoverablePopover>
              }
              listenOnChange={handleAllowNestingChange}
              justifyContent={'flex-end'}
            />
            <ReduxSelect
              isDisabled={!allowNesting}
              label={'Maximum Nested Level'}
              name={'maximumNestedLevelsCount'}
              options={levelsCountOptions as unknown as IOption[]}
              onSelectChange={handleMaximumNestedLevelsCountChange}
            />
            <FieldArray name={'levels'} component={Levels} onPanelClick={onPanelClick} />
          </FieldsContainer>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={isSecurityOpen} onChange={(_, expanded) => setIsSecurityOpen(expanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon htmlColor={colors.blue500} />}>Security</AccordionSummary>
        <AccordionDetails>
          <FieldsWrapper>
            <ReduxEntitySelect
              label={'Plan Owner Security Assignment'}
              name={'planOwnerObjectSecurity'}
              query={OBJECT_SECURITIES_QUERY}
              variables={{ filter: { objectEntityApiName: 'plans' } }}
              {...objectSecurityProps}
            />
            <ReduxEntitySelect
              label={'Default Plan Member Security Assignment'}
              name={'planMemberObjectSecurity'}
              query={OBJECT_SECURITIES_QUERY}
              variables={{ filter: { objectEntityApiName: 'plans' } }}
              {...objectSecurityProps}
            />
            <ReduxEntitySelect
              label={'Objective Owner Security Assignment'}
              name={'objectiveOwnerObjectSecurity'}
              query={OBJECT_SECURITIES_QUERY}
              variables={{ filter: { objectEntityApiName: 'objectives' } }}
              {...objectSecurityProps}
            />
            <ReduxEntitySelect
              label={'Default Objective Member Security Assignment'}
              name={'objectiveMemberObjectSecurity'}
              query={OBJECT_SECURITIES_QUERY}
              variables={{ filter: { objectEntityApiName: 'objectives' } }}
              {...objectSecurityProps}
            />
            <ReduxEntitySelect
              label={'Indicator Owner Security Assignment'}
              name={'kpiOwnerObjectSecurity'}
              query={OBJECT_SECURITIES_QUERY}
              variables={{ filter: { objectEntityApiName: 'kpis' } }}
              {...objectSecurityProps}
            />
            <ReduxEntitySelect
              label={'Default Indicator Member Security Assignment'}
              name={'kpiMemberObjectSecurity'}
              query={OBJECT_SECURITIES_QUERY}
              variables={{ filter: { objectEntityApiName: 'kpis' } }}
              {...objectSecurityProps}
            />
            <ReduxEntitySelect
              label={'Action Owner Security Assignment'}
              name={'actionOwnerObjectSecurity'}
              query={OBJECT_SECURITIES_QUERY}
              variables={{ filter: { objectEntityApiName: 'actions' } }}
              {...objectSecurityProps}
            />
            <ReduxEntitySelect
              label={'Default Action Member Security Assignment'}
              name={'actionMemberObjectSecurity'}
              query={OBJECT_SECURITIES_QUERY}
              variables={{ filter: { objectEntityApiName: 'actions' } }}
              {...objectSecurityProps}
            />
          </FieldsWrapper>
        </AccordionDetails>
      </Accordion>
      <SetColorModal />
    </Form>
  )
}

export default reduxForm<any, Props>({
  form: Forms.PLAN_TYPE_EDIT_FORM,
  enableReinitialize: true
})(PlanTypeForm)
